/**
 * nationalTaxEngine.ts
 *
 * 【役割】所得税（国税）の税額計算エンジン
 *   - incomeEngine.ts で算出した所得金額に、税率・所得控除・税額控除を適用して納付税額を出す
 *   - 復興特別所得税（2.1%）の加算もここで行う
 *
 * 【将来の拡張イメージ】
 *   住民税: residentTaxEngine.ts を新設し incomeEngine を同様に呼び出す
 *     → 基礎控除 43 万・フラット税率 10%（道府県 4%＋市町村 6%）・調整控除 など
 *   社会保険料: socialInsuranceEngine.ts を新設
 *     → 標準報酬月額テーブルを持ち incomeEngine の給与所得を入力に使う
 *
 * 【依存関係】
 *   incomeEngine.ts  → 所得金額（共有）
 *   taxRateCalc.ts   → 累進税率速算表
 *   taxDeducCalc.ts  → 青色申告特別控除額の判定
 *   taxCreditCalc.ts → 住宅借入金特別控除（税額控除）
 */

import * as incomeEngine from './incomeEngine';
import * as taxRateCalc from './taxRateCalc';
import * as taxDeducCalc from './taxDeducCalc';
import * as taxCreditCalc from './taxCreditCalc';
import type { 全収入情報, 山林所得計算のための情報, 不動産の譲渡所得計算のための情報 } from './taxCalcInput';

// ---------------------------------------------------------------------------
// 分離課税：山林所得の税額（5分5乗方式）
// ---------------------------------------------------------------------------

export type 山林所得税額計算結果 = {
  山林所得: number;
  /** 5分した課税ベース（表示用） */
  課税山林所得: number;
  山林所得税額: number;
};

/**
 * 5分5乗方式:
 *   1. 山林所得 ÷ 5 = 課税山林所得
 *   2. 課税山林所得に累進税率を適用
 *   3. 税額 × 5 = 山林所得税額
 *   4. 復興特別所得税を加算
 */
export function 山林所得税額計算関数(
  input: 山林所得計算のための情報,
  青色申告控除額: number,
): 山林所得税額計算結果 {
  const { 山林所得 } = incomeEngine.山林所得計算関数(input, 青色申告控除額);

  if (山林所得 <= 0) {
    return { 山林所得: Math.max(0, 山林所得), 課税山林所得: 0, 山林所得税額: 0 };
  }

  const 課税山林所得 = Math.floor(山林所得 / 5);
  const 暫定税額 = taxRateCalc.税額計算関数(課税山林所得);
  const 山林所得税額 = taxRateCalc.復興所得税考慮関数(暫定税額 * 5);

  return { 山林所得, 課税山林所得, 山林所得税額 };
}

// ---------------------------------------------------------------------------
// 分離課税：不動産の譲渡所得の税額
// ---------------------------------------------------------------------------

export type 不動産の譲渡所得税額計算結果 = {
  不動産の譲渡所得: number;
  不動産の課税譲渡所得: number;
  不動産の譲渡所得税額: number;
};

/**
 * 不動産の譲渡所得税（復興税込み簡易レート）:
 *   長期（5年超）: 15.315%（所得税 15% + 復興特別所得税 0.315%）
 *   短期（5年以下）: 30.63%（所得税 30% + 復興特別所得税 0.63%）
 * ※ 3000万円特別控除など個別特例は未考慮
 */
export function 不動産の譲渡所得税額計算関数(
  input: 不動産の譲渡所得計算のための情報,
): 不動産の譲渡所得税額計算結果 {
  const { 不動産の譲渡所得 } = incomeEngine.不動産の譲渡所得計算関数(input);
  const 税率 = input.所有期間は5年超か ? 0.15315 : 0.3063;
  return {
    不動産の譲渡所得,
    不動産の課税譲渡所得: 不動産の譲渡所得,
    不動産の譲渡所得税額: 不動産の譲渡所得 * 税率,
  };
}

// ---------------------------------------------------------------------------
// 総合課税：課税所得 → 所得税額
// ---------------------------------------------------------------------------

export type 総合課税額計算結果 = {
  給与所得: number;
  一時所得: number;
  雑所得: number;
  不動産所得: number;
  事業所得: number;
  不動産以外の譲渡所得: number;
  総所得: number;
  課税給与所得: number;
  給与所得税額: number;
};

/**
 * 総合課税の所得税額を計算する
 * 返り値に「青色控除の残枠」を含め、山林所得計算へバトンパスする
 *
 * 控除の適用（所得税の場合）:
 *   基礎控除        62万（令和7年度改正後の恒久額）
 *   基礎控除の特例  総所得 ≤ 489万 → +42万 / それ以外 → +5万
 *   勤労学生控除    学生 かつ 総所得 ≤ 89万 → 27万
 *
 * ※ 住民税の基礎控除は 43万（特例は別途 residentTaxEngine で定義すること）
 */
export function 総合課税額計算関数(
  input: 全収入情報,
  青色申告控除額: number,
): {
  総合課税額結果: 総合課税額計算結果;
  不動産所得と事業所得を考慮後の青色申告控除額: number;
} {
  const 給与所得 = incomeEngine.給与所得計算関数(input.給与収入);

  const 一時所得 = input.一時所得計算のための情報
    ? incomeEngine.一時所得計算関数(input.一時所得計算のための情報)
    : 0;

  const 不動産以外の譲渡所得 = input.不動産以外の譲渡所得計算のための情報
    ? incomeEngine.不動産以外の譲渡所得計算関数(input.不動産以外の譲渡所得計算のための情報)
    : 0;

  const { 不動産所得, 不動産所得を考慮後の青色申告控除額 } = input.不動産所得計算のための情報
    ? incomeEngine.不動産所得計算関数(input.不動産所得計算のための情報, 青色申告控除額)
    : { 不動産所得: 0, 不動産所得を考慮後の青色申告控除額: 青色申告控除額 };

  const { 事業所得, 不動産所得と事業所得を考慮後の青色申告控除額 } =
    input.事業所得計算のための情報
      ? incomeEngine.事業所得計算関数(
          input.事業所得計算のための情報,
          不動産所得を考慮後の青色申告控除額,
        )
      : { 事業所得: 0, 不動産所得と事業所得を考慮後の青色申告控除額: 青色申告控除額 };

  const 雑所得 = input.雑所得計算のための情報
    ? incomeEngine.雑所得計算関数(input.雑所得計算のための情報, input.年齢)
    : 0;

  // 一時所得は総所得算入時に 1/2
  const 総所得 =
    給与所得 + 一時所得 / 2 + 雑所得 + 不動産所得 + 事業所得 + 不動産以外の譲渡所得;

  // 所得控除（国税の基礎控除）
  const 基礎控除 = 620_000;
  const 基礎控除の特例 = 総所得 <= 4_890_000 ? 420_000 : 50_000;
  const 勤労学生控除 = input.学生かどうか && 総所得 <= 890_000 ? 270_000 : 0;
  const 課税給与所得 = Math.max(0, 総所得 - 基礎控除 - 基礎控除の特例 - 勤労学生控除);

  // 税額（復興特別所得税込み）
  const 給与所得税額 = taxRateCalc.復興所得税考慮関数(taxRateCalc.税額計算関数(課税給与所得));

  return {
    総合課税額結果: {
      給与所得,
      一時所得,
      雑所得,
      不動産所得,
      事業所得,
      不動産以外の譲渡所得,
      総所得,
      課税給与所得,
      給与所得税額,
    },
    不動産所得と事業所得を考慮後の青色申告控除額,
  };
}

// ---------------------------------------------------------------------------
// 納付税額（所得税の総合計算エントリーポイント）
// ---------------------------------------------------------------------------

export type 納付税額計算結果 = {
  総合課税額結果: 総合課税額計算結果;
  山林所得結果: 山林所得税額計算結果;
  不動産の譲渡所得結果: 不動産の譲渡所得税額計算結果;
  納付税額: number;
};

/**
 * 所得税の納付税額（試算）
 *
 * 計算フロー:
 *   1. 青色申告特別控除額の決定
 *   2. 総合課税（給与・雑・事業・不動産・一時・不動産以外譲渡）→ 給与所得税額
 *   3. 分離課税・山林所得 → 山林所得税額（5分5乗）
 *   4. 分離課税・不動産譲渡 → 不動産の譲渡所得税額
 *   5. 住宅借入金特別控除（税額控除）を差し引く
 *   6. 合計 = max(0, 総合 + 山林 + 不動産譲渡 - 住宅控除)
 *
 * ※ 源泉徴収済みの配当・退職・利子所得は含めない（別途申告不要扱い）
 */
export function 納付税額計算関数(input: 全収入情報): 納付税額計算結果 {
  const 青色申告控除額 = input.青色申告情報
    ? taxDeducCalc.青色申告特別控除額計算関数(input.青色申告情報)
    : 0;

  const { 総合課税額結果, 不動産所得と事業所得を考慮後の青色申告控除額 } =
    総合課税額計算関数(input, 青色申告控除額);

  const 山林所得結果 = input.山林所得計算のための情報
    ? 山林所得税額計算関数(
        input.山林所得計算のための情報,
        不動産所得と事業所得を考慮後の青色申告控除額,
      )
    : { 山林所得: 0, 課税山林所得: 0, 山林所得税額: 0 };

  const 不動産の譲渡所得結果 = input.不動産の譲渡所得計算のための情報
    ? 不動産の譲渡所得税額計算関数(input.不動産の譲渡所得計算のための情報)
    : { 不動産の譲渡所得: 0, 不動産の課税譲渡所得: 0, 不動産の譲渡所得税額: 0 };

  const 合計所得 =
    総合課税額結果.総所得 +
    山林所得結果.山林所得 +
    不動産の譲渡所得結果.不動産の譲渡所得;

  let 住宅借入金特別控除額 = 0;
  if (input.住宅情報) {
    if (taxCreditCalc.住宅借入金特別控除使用可否判断関数(input, 合計所得)) {
      住宅借入金特別控除額 = taxCreditCalc.住宅借入金特別控除額計算関数(input);
    }
  }

  const 納付税額 = Math.max(
    0,
    総合課税額結果.給与所得税額 +
      山林所得結果.山林所得税額 +
      不動産の譲渡所得結果.不動産の譲渡所得税額 -
      住宅借入金特別控除額,
  );

  return { 総合課税額結果, 山林所得結果, 不動産の譲渡所得結果, 納付税額 };
}
